import DatePicker from './c-date-picker';
import { breakpoints } from '../config/breakpoints';
import { formatTime, formatPrice } from '../utils/format';

export default class ExperienceWidget {
  static mobileMQ = window.matchMedia(`(width <= ${breakpoints.md}px)`);

  static get isMobile() {
    return ExperienceWidget.mobileMQ.matches;
  }

  static init() {
    const widgets = document.querySelectorAll('.c-booking-widget');
    [...widgets].forEach((widget) => {
      /* eslint-disable-next-line no-param-reassign */
      widget.experienceWidget = new ExperienceWidget(widget);
    });
  }

  get isExpanded() {
    return this.expandButton.ariaExpanded === 'true';
  }

  get checkedTimeSlot() {
    return [...(this.form?.querySelectorAll('input[name="experience-widget-time"]') ?? [])]
      .find((r) => r.checked) ?? null;
  }

  /** Open-date requests let the user pick any future date — no availability/timeslots/price. */
  get isOpenDate() {
    return this.reservationMode === 'on-request-open-date';
  }

  constructor(element) {
    this.element = element;
    this.isSummary = this.element.classList.contains('c-booking-widget--is-summary');
    this.header = this.element.querySelector('.c-booking-widget__header');
    this.headerDesc = this.element.querySelector('.c-booking-widget__header-desc');
    this.price = this.element.querySelector('.c-booking-widget__experience-total-price');
    this.expandButton = this.element.querySelector('.c-booking-widget__expand');
    this.expandableSection = this.element.querySelector('.c-booking-widget__expandable');
    this.form = this.element.querySelector('.c-booking-widget__experience-form');
    this.submitButton = this.element.querySelector('.c-booking-widget__experience-submit');
    this.formElementNames = [];

    // Booking API state (read from form data attributes set by PHP)
    this.bookingCode = this.form?.dataset.bookingCode ?? '';
    this.reservationMode = this.form?.dataset.reservationMode ?? '';
    this.restUrl = this.form?.dataset.restUrl ?? '';
    this.restNonce = this.form?.dataset.restNonce ?? '';
    this.experienceStepsUrl = this.form?.dataset.experienceStepsUrl ?? '';
    try {
      this.sessionData = this.form?.dataset.sessionData
        ? JSON.parse(this.form.dataset.sessionData) : {};
    } catch {
      this.sessionData = {};
    }
    /** @type {Map<string, object[]>} ISO date → sessions array from API */
    this.availabilityByDate = new Map();
    /** Currency code from the booking engine availability response (e.g. "MXN"). */
    this.currency = '';
    /** @type {import('./c-date-picker').default|null} */
    this.datePicker = null;
    /** @type {HTMLElement|null} */
    this.radioGroupEl = null;
    this.currentAvailFetchController = null;
    /** @type {HTMLElement|null} */
    this.totalSection = this.element.querySelector('.c-booking-widget__experience-total');
    this.capacityErrorShown = false;

    ExperienceWidget.mobileMQ.addEventListener('change', this.updateForBreakpoint);

    window.addEventListener('resize', this.setHeaderHeightCSSVariable);

    this.expandButton.addEventListener('click', this.togglePanel);

    this.updateForBreakpoint();

    [...this.element.querySelectorAll('.c-booking-widget__experience-input--radio-group')]
      .forEach(this.initRadioGroup);

    [...this.element.querySelectorAll('.c-booking-widget__experience-input--date')]
      .forEach(this.initDatePicker);

    [...this.element.querySelectorAll('.c-booking-widget__experience-input--stepper')]
      .forEach(this.initStepper);

    if (this.form) {
      this.form.addEventListener('change', this.validateForm);
      this.form.addEventListener('submit', this.handleFormSubmit);
    }

    if (this.bookingCode && this.restUrl && !this.isOpenDate) {
      // Re-fetch on guest count change — availability modes only, debounced 500 ms.
      const guestsInput = this.element.querySelector('input[name="experience-widget-guests"]');
      if (guestsInput) {
        let guestsFetchTimer;
        guestsInput.addEventListener('change', () => {
          clearTimeout(guestsFetchTimer);
          guestsFetchTimer = setTimeout(async () => {
            // On-demand with a slot already selected: re-apply the slot's capacity
            // constraints without re-fetching — preserves the selection.
            if (this.reservationMode === 'on-demand' && this.checkedTimeSlot) {
              this.applySlotCapacity(this.checkedTimeSlot);
              return;
            }

            const year = this.datePicker?.visibleYear ?? new Date().getFullYear();
            const month = this.datePicker?.visibleMonth ?? new Date().getMonth();
            await this.fetchMonthAvailability(year, month);
            const dateInput = this.form?.elements['experience-widget-date'];
            if (dateInput?.value) {
              const sessions = this.availabilityByDate.get(dateInput.value) ?? [];
              if (sessions.length === 0) {
                // Date no longer available for the new guest count — reset selection.
                dateInput.value = '';
                if (this.reservationMode === 'on-demand') {
                  this.renderTimeSlots([]);
                }
                this.clearCapacityError();
                if (this.form) this.form.dispatchEvent(new Event('change'));
              } else if (this.reservationMode === 'on-demand') {
                this.checkDateCapacity(sessions);
                this.renderTimeSlots(sessions);
                const restoredSlot = this.checkedTimeSlot;
                if (restoredSlot) {
                  this.applySlotCapacity(restoredSlot);
                }
              } else {
                this.recheckDateCapacity(dateInput.value);
              }
            }
          }, 500);
        });
      }

      const now = new Date();
      this.fetchMonthAvailability(now.getFullYear(), now.getMonth());
    }

    // Restore prior selection (date / guests / time slot) after the browser's
    // form-restoration pass on back-navigation. Using pageshow covers both
    // fresh loads and bfcache restores. Open-date has no availability to fetch
    // but still restores the chosen date/guests.
    if (this.bookingCode && this.restUrl) {
      window.addEventListener('pageshow', () => { this.restoreFromSession(); });
    }
  }

  restoreFromSession = async () => {
    let saved;
    try {
      const raw = sessionStorage.getItem('azds_booking');
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      return;
    }

    if (!saved || saved.booking_code !== this.bookingCode) return;

    const guestsInput = this.form?.elements['experience-widget-guests'];
    if (guestsInput && saved.guests) {
      // Set value directly — dispatching 'change' would trigger the debounced
      // guest-fetch handler and race the explicit fetch below.
      guestsInput.value = String(saved.guests);
      guestsInput.style.width = `${guestsInput.value.length + 2}ch`;
      this.refreshStepperButtons();
    }

    // Open-date: no availability to reconcile — just restore the chosen date.
    if (this.isOpenDate) {
      if (saved.date && this.datePicker) this.datePicker.setSelectedDate(saved.date);
      if (this.form) this.form.dispatchEvent(new Event('change'));
      return;
    }

    if (!saved.date || !this.datePicker) {
      if (this.form) this.form.dispatchEvent(new Event('change'));
      return;
    }

    const dateInput = this.form?.elements['experience-widget-date'];
    if (!dateInput) return;

    this.datePicker.setSelectedDate(saved.date);

    const [yearStr, monthStr] = saved.date.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    if (Number.isFinite(year) && Number.isFinite(month)) {
      await this.fetchMonthAvailability(year, month);
    }

    const sessions = this.availabilityByDate.get(saved.date) ?? [];

    if (sessions.length === 0) {
      dateInput.value = '';
      this.datePicker.selectedDate = null;
      this.datePicker.syncPillLabel();
      if (!this.datePicker.picker.hidden) this.datePicker.render();
      this.clearCapacityError();
      if (this.radioGroupEl) {
        [...this.radioGroupEl.querySelectorAll('label.c-booking-widget__experience-input--radio')]
          .forEach((el) => el.remove());
      }
      if (this.form) this.form.dispatchEvent(new Event('change'));
      return;
    }

    this.checkDateCapacity(sessions);

    if (this.reservationMode === 'on-demand') {
      this.renderTimeSlots(sessions);
      if (saved.session_code && this.radioGroupEl) {
        const radio = this.radioGroupEl.querySelector(
          `input[type="radio"][value="${CSS.escape(saved.session_code)}"]`,
        );
        if (radio && !radio.disabled) {
          radio.checked = true;
          this.applySlotCapacity(radio);
        }
      }
    }

    if (this.form) this.form.dispatchEvent(new Event('change'));
  };

  expandPanel = () => {
    if (!ExperienceWidget.isMobile && !this.isExpanded) {
      return;
    }

    this.expandButton.ariaExpanded = 'true';
    this.expandableSection.classList.remove('c-booking-widget__expandable--minimized');
    this.expandableSection.removeAttribute('inert');
    this.expandButton.focus();

    if (this.isSummary) {
      this.expandButton.ariaLabel = this.expandButton.dataset.closeLabel;
    } else {
      this.expandButton.ariaLabel = this.expandButton.querySelector('.c-booking-widget__expand--button-close').textContent;

      const header = document.querySelector('header.header');
      if (header) {
        header.style.zIndex = 0;
      }

      document.addEventListener('keydown', this.closeOnEscape);
      document.addEventListener('focusin', this.lockFocus);

      document.body.classList.add('lock-scroll');
    }
  };

  minimizePanel = () => {
    if (!ExperienceWidget.isMobile && this.isExpanded) {
      return;
    }

    this.expandButton.ariaExpanded = 'false';
    this.expandableSection.classList.add('c-booking-widget__expandable--minimized');
    this.expandableSection.setAttribute('inert', '');
    this.expandButton.focus();

    if (this.isSummary) {
      this.expandButton.ariaLabel = this.expandButton.dataset.openLabel;
    } else {
      this.expandButton.ariaLabel = this.expandButton.querySelector('.c-booking-widget__expand--button-open').textContent;
    }

    const header = document.querySelector('header.header');
    if (header) {
      header.style.zIndex = '';
    }

    document.removeEventListener('keydown', this.closeOnEscape);
    document.removeEventListener('focusin', this.lockFocus);

    document.body.classList.remove('lock-scroll');
  };

  togglePanel = () => {
    if (this.isExpanded) {
      this.minimizePanel();
    } else {
      this.expandPanel();
    }
  };

  updateForBreakpoint = () => {
    this.setHeaderHeightCSSVariable();

    if (ExperienceWidget.isMobile && !this.isSummary) {
      this.minimizePanel();
    } else {
      this.expandPanel();
    }

    if (!ExperienceWidget.isMobile) {
      this.expandableSection.removeAttribute('inert');
      document.body.classList.remove('lock-scroll');
    }
  };

  closeOnEscape = (event) => {
    if (event.key === 'Escape') {
      this.minimizePanel();
    }
  };

  lockFocus = (event) => {
    const focusedElement = document.activeElement;
    const lastElement = event.relatedTarget;

    if (!focusedElement.closest('.c-booking-widget')) {
      const focusable = this.element.querySelectorAll('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');

      if (lastElement === focusable[0]) {
        focusable[focusable.length - 1].focus();
      } else {
        focusable[0].focus();
      }
    }
  };

  setHeaderHeightCSSVariable = () => {
    const headerHeight = this.header.offsetHeight;
    const headerDescHeight = this.headerDesc ? this.headerDesc.offsetHeight : 0;
    const gap = this.headerDesc ? parseInt(window.getComputedStyle(this.header).rowGap, 10) : 0;

    document.documentElement.style.setProperty('--widget-header-height', `${headerHeight - headerDescHeight - gap}px`);
  };

  validateForm = () => {
    if (!this.form || !this.formElementNames.length) {
      return;
    }

    const anyBlank = this.formElementNames.some((name) => {
      const el = this.form.elements[name];
      if (!el) return true;
      // A single <input type="radio"> returns its value attribute even when unchecked.
      // Must check .checked explicitly; RadioNodeList.value already returns '' when none checked.
      if (el instanceof HTMLInputElement && el.type === 'radio') return !el.checked;
      return el.value === '';
    });

    if (anyBlank || this.capacityErrorShown) {
      this.submitButton.disabled = true;
    } else {
      this.submitButton.removeAttribute('disabled');
    }

    this.updatePrice();
  };

  updatePrice = () => {
    if (!this.price) return;

    // Price/Total are shown for on-demand only; request modes hide them.
    if (this.reservationMode !== 'on-demand') {
      this.price.textContent = '';
      if (this.totalSection) this.totalSection.hidden = true;
      return;
    }

    const guestsInput = this.form?.elements['experience-widget-guests'];
    const guests = guestsInput ? (parseInt(guestsInput.value, 10) || 1) : 1;

    if (!this.capacityErrorShown && this.form) {
      if (this.reservationMode === 'on-demand') {
        const checked = this.checkedTimeSlot;
        if (checked?.dataset.price) {
          const amount = parseFloat(checked.dataset.price);
          if (amount) {
            const total = amount * guests;
            this.price.textContent = formatPrice(total, this.currency);
            if (this.totalSection) this.totalSection.hidden = false;
            return;
          }
        }
      }
    }

    this.price.textContent = '';
    if (this.totalSection) this.totalSection.hidden = true;
  };

  initRadioGroup = (radioGroup) => {
    this.radioGroupEl = radioGroup;
    const inputName = radioGroup.dataset.inputName
      ?? radioGroup.querySelector('input[type="radio"]')?.name;
    if (inputName) {
      this.formElementNames.push(inputName);
    }
    // For on-demand: tighten the stepper max to the selected slot's availability.
    if (this.reservationMode === 'on-demand') {
      radioGroup.addEventListener('change', (e) => {
        if (e.target.type === 'radio' && e.target.checked) {
          this.applySlotCapacity(e.target);
        }
      });
    }
  };

  initDatePicker = (datePicker) => {
    const input = datePicker.querySelector('input[type="date"]');
    const pill = datePicker.querySelector('.c-booking-widget__experience-input--date-pill');
    const mount = datePicker.parentElement.querySelector('[data-date-picker-root]');

    if (!input || !pill) {
      return;
    }

    this.formElementNames.push(input.name);

    const picker = new DatePicker({
      input,
      pill,
      root: datePicker,
      mount,
    });

    this.datePicker = picker;
    datePicker.addEventListener('datepicker:monthchange', this.handleMonthChange);
    input.addEventListener('change', this.handleDateChange);

    input.addEventListener('click', (event) => {
      if (event.target.closest('.c-booking-widget__calendar')) {
        return;
      }

      picker.toggleOpen();
    });

    pill.addEventListener('click', () => {
      picker.toggleOpen();
    });
  };

  initStepper = (stepper) => {
    const input = stepper.querySelector('input[type="number"]');
    const decrementButton = stepper.querySelector('.c-booking-widget__experience-input--stepper-down');
    const incrementButton = stepper.querySelector('.c-booking-widget__experience-input--stepper-up');

    this.formElementNames.push(input.name);

    const min = parseInt(input.min, 10) || 0;

    if (input.max && !input.hasAttribute('data-original-max')) {
      input.setAttribute('data-original-max', input.max);
    }

    const getMax = () => parseInt(input.max, 10) || Infinity;

    function setStepperDisabled() {
      decrementButton.disabled = parseInt(input.value, 10) <= min;
      incrementButton.disabled = parseInt(input.value, 10) >= getMax();
    }

    function updateInputWidth() {
      input.style.width = `${input.value.length + 2}ch`;
    }

    function syncStepper() {
      updateInputWidth();
      setStepperDisabled();
    }

    input.addEventListener('change', () => {
      const value = parseInt(input.value, 10);
      if (value < min) {
        input.value = min;
      } else if (value > getMax()) {
        input.value = getMax();
      }

      updateInputWidth();
      setStepperDisabled();
    });

    input.addEventListener('focus', () => {
      input.select();
    });

    decrementButton.addEventListener('click', () => {
      input.value = Math.max(parseInt(input.value, 10) - 1, min);

      input.dispatchEvent(new Event('change'));
      this.form.dispatchEvent(new Event('change'));
    });

    incrementButton.addEventListener('click', () => {
      input.value = Math.min(parseInt(input.value, 10) + 1, getMax());

      input.dispatchEvent(new Event('change'));
      this.form.dispatchEvent(new Event('change'));
    });

    window.addEventListener('pageshow', syncStepper);
    syncStepper();
  };

  // ─── Booking API integration ──────────────────────────────────────────────

  handleMonthChange = (event) => {
    if (this.bookingCode && this.restUrl && !this.isOpenDate) {
      this.fetchMonthAvailability(event.detail.year, event.detail.month);
    }
  };

  handleDateChange = (event) => {
    const selectedDate = event.target.value;

    if (!selectedDate) {
      this.clearCapacityError();
      if (this.radioGroupEl) {
        [...this.radioGroupEl.querySelectorAll('label.c-booking-widget__experience-input--radio')]
          .forEach((el) => el.remove());
      }
      if (this.form) this.form.dispatchEvent(new Event('change'));
      return;
    }

    const sessions = this.availabilityByDate.get(selectedDate) ?? [];
    this.checkDateCapacity(sessions);

    if (this.reservationMode === 'on-demand') {
      this.renderTimeSlots(sessions);
      // renderTimeSlots dispatches form change
    } else if (this.form) {
      this.form.dispatchEvent(new Event('change'));
    }
  };

  recheckDateCapacity = (date) => {
    const sessions = this.availabilityByDate.get(date) ?? [];
    this.checkDateCapacity(sessions);
    if (this.form) this.form.dispatchEvent(new Event('change'));
  };

  checkDateCapacity = (sessions) => {
    if (!sessions.length) {
      this.clearCapacityError();
      return;
    }

    const guestsInput = this.form?.elements['experience-widget-guests'];
    const guests = guestsInput ? (parseInt(guestsInput.value, 10) || 1) : 1;
    const maxAvailable = sessions.reduce((max, s) => Math.max(max, s.availability ?? 0), 0);

    if (guestsInput) {
      const originalMax = parseInt(guestsInput.getAttribute('data-original-max') ?? '', 10)
        || Infinity;
      const effectiveMax = Number.isFinite(originalMax)
        ? Math.min(maxAvailable, originalMax) : maxAvailable;
      guestsInput.max = effectiveMax > 0 ? String(effectiveMax) : '0';

      // Clamp current value if it now exceeds available capacity.
      // Use refreshStepperButtons instead of dispatching 'change' to avoid
      // re-triggering the debounced guest fetch handler.
      if (guests > effectiveMax && effectiveMax > 0) {
        guestsInput.value = String(effectiveMax);
        this.refreshStepperButtons();
      }
    }

    if (maxAvailable < guests) {
      this.showCapacityError(maxAvailable);
    } else {
      const el = this.element.querySelector('.c-booking-widget__capacity-error');
      if (el) { el.hidden = true; el.textContent = ''; }
      this.capacityErrorShown = false;
    }
  };

  showCapacityError = (available) => {
    const el = this.element.querySelector('.c-booking-widget__capacity-error');
    if (!el) return;
    el.textContent = available === 0
      ? 'Sorry, there are no available spots for this date.'
      : `Only ${available} spot${available !== 1 ? 's' : ''} available for this date.`;
    el.hidden = false;
    this.capacityErrorShown = true;
  };

  clearCapacityError = () => {
    const el = this.element.querySelector('.c-booking-widget__capacity-error');
    if (el) { el.hidden = true; el.textContent = ''; }
    this.capacityErrorShown = false;

    const guestsInput = this.form?.elements['experience-widget-guests'];
    if (guestsInput) {
      guestsInput.max = guestsInput.getAttribute('data-original-max') ?? '';
      // Don't dispatch 'change' on guests — would trigger a debounced re-fetch.
      this.refreshStepperButtons();
    }
  };

  /** Update +/− button states without dispatching a 'change' event. */
  refreshStepperButtons = () => {
    const guestsInput = this.form?.elements['experience-widget-guests'];
    if (!guestsInput) return;
    const min = parseInt(guestsInput.min, 10) || 0;
    const max = parseInt(guestsInput.max, 10) || Infinity;
    const val = parseInt(guestsInput.value, 10);
    const wrapper = guestsInput.closest('.c-booking-widget__experience-input--stepper');
    if (!wrapper) return;
    const decBtn = wrapper.querySelector('.c-booking-widget__experience-input--stepper-down');
    const incBtn = wrapper.querySelector('.c-booking-widget__experience-input--stepper-up');
    if (decBtn) decBtn.disabled = val <= min;
    if (incBtn) incBtn.disabled = val >= max;
  };

  handleFormSubmit = (event) => {
    event.preventDefault();

    const guestsInput = this.form.elements['experience-widget-guests'];
    const dateInput = this.form.elements['experience-widget-date'];
    const guests = guestsInput ? (parseInt(guestsInput.value, 10) || 1) : 1;
    const selectedDate = dateInput ? dateInput.value : '';

    let selectedSession = null;
    let sessionCode = '';
    let price = 0;

    if (this.reservationMode === 'on-demand') {
      const checked = this.checkedTimeSlot;
      if (checked) {
        sessionCode = checked.value;
        price = (parseFloat(checked.dataset.price) || 0) * guests;
        try {
          selectedSession = JSON.parse(decodeURIComponent(checked.dataset.session));
        } catch { /* ignore */ }
      }
    } else if (this.reservationMode === 'on-request' && selectedDate) {
      const sessions = this.availabilityByDate.get(selectedDate) ?? [];
      const firstSession = sessions[0];
      if (firstSession?.price) {
        price = (parseFloat(firstSession.price) || 0) * guests;
      }
    }

    const bookingData = {
      booking_code: this.bookingCode,
      reservation_mode: this.reservationMode,
      session_code: sessionCode,
      date: selectedDate,
      guests,
      price,
      currency: this.currency,
      session: selectedSession,
      session_data: this.sessionData,
      rest_url: this.restUrl,
      rest_nonce: this.restNonce,
    };

    try {
      sessionStorage.setItem('azds_booking', JSON.stringify(bookingData));
    } catch { /* sessionStorage unavailable */ }

    if (this.experienceStepsUrl) {
      const stepsUrl = new URL(this.experienceStepsUrl);
      stepsUrl.searchParams.set('reservation_mode', this.reservationMode);
      if (this.sessionData?.meal_enabled) {
        stepsUrl.searchParams.set('enable_meal', '1');
      }
      window.location.href = stepsUrl.toString();
    }
  };

  fetchMonthAvailability = async (year, month) => {
    if (this.currentAvailFetchController) {
      this.currentAvailFetchController.abort();
    }
    this.currentAvailFetchController = new AbortController();

    const pad = (n) => String(n).padStart(2, '0');
    const lastDay = new Date(year, month + 1, 0).getDate();
    const startDate = `${year}-${pad(month + 1)}-01`;
    const endDate = `${year}-${pad(month + 1)}-${pad(lastDay)}`;

    const guestsInput = this.form?.elements['experience-widget-guests'];
    const guestAmount = guestsInput ? (parseInt(guestsInput.value, 10) || 1) : 1;

    const url = new URL(
      `${this.restUrl}/experiences/${encodeURIComponent(this.bookingCode)}/availability`,
    );
    url.searchParams.set('startDate', startDate);
    url.searchParams.set('endDate', endDate);
    if (guestAmount >= 1) url.searchParams.set('guestAmount', String(guestAmount));

    try {
      const response = await fetch(url.toString(), {
        headers: { 'X-WP-Nonce': this.restNonce },
        signal: this.currentAvailFetchController.signal,
      });

      if (!response.ok) {
        // 400 = no sessions for range — show no available dates
        this.updateAvailableDates(new Set());
        return;
      }

      const data = await response.json();
      // Clear stale entries for this month before repopulating.
      [...this.availabilityByDate.keys()].forEach((date) => {
        const [y, m] = date.split('-').map(Number);
        if (y === year && m === month + 1) {
          this.availabilityByDate.delete(date);
        }
      });
      data.forEach((dayEntry) => {
        this.availabilityByDate.set(dayEntry.date, dayEntry.sessions ?? []);
      });
      const currency = data.find((d) => d.currency)?.currency;
      if (currency) this.currency = currency;
      this.updateAvailableDates(new Set(data.map((d) => d.date)));
    } catch (err) {
      if (err.name !== 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('Availability fetch failed:', err);
      }
    }
  };

  updateAvailableDates = (isoSet) => {
    if (!this.datePicker) return;
    this.datePicker.setAvailableDates(isoSet);
  };

  renderTimeSlots = (sessions) => {
    if (!this.radioGroupEl) return;

    const previousValue = this.checkedTimeSlot?.value ?? null;

    [...this.radioGroupEl.querySelectorAll('label.c-booking-widget__experience-input--radio')]
      .forEach((el) => el.remove());

    if (sessions.length === 0) {
      if (this.form) this.form.dispatchEvent(new Event('change'));
      return;
    }

    // Deduplicate by start+end time, keeping highest availability.
    const seen = new Set();
    const uniqueSessions = [...sessions]
      .sort((a, b) => (b.availability ?? 0) - (a.availability ?? 0))
      .filter((session) => {
        const key = `${session.startTime}|${session.endTime}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    uniqueSessions.forEach((session) => {
      const label = document.createElement('label');
      label.className = 'c-booking-widget__experience-input c-booking-widget__experience-input--radio';

      const timeLabel = this.formatTime(session.startTime)
        + (session.endTime ? ` – ${this.formatTime(session.endTime)}` : '');
      const isUnavailable = !session.availability || session.availability <= 0;

      label.innerHTML = `
        <input
          name="experience-widget-time"
          type="radio"
          value="${session.sessionCode}"
          data-price="${session.price ?? ''}"
          data-session="${encodeURIComponent(JSON.stringify(session))}"
          data-target-input
          ${isUnavailable ? 'disabled' : ''}
        />
        <span>${timeLabel}</span>
      `;

      this.radioGroupEl.appendChild(label);
    });

    if (previousValue) {
      const match = this.radioGroupEl.querySelector(
        `input[type="radio"][value="${CSS.escape(previousValue)}"]`,
      );
      if (match && !match.disabled) {
        match.checked = true;
      }
    }

    if (this.form) {
      this.form.dispatchEvent(new Event('change'));
    }
  };

  /** Tighten stepper max to the selected slot's availability. */
  applySlotCapacity = (radioEl) => {
    let availability = Infinity;
    try {
      const session = JSON.parse(decodeURIComponent(radioEl.dataset.session ?? ''));
      if (typeof session.availability === 'number') {
        availability = session.availability;
      }
    } catch { /* ignore */ }

    const guestsInput = this.form?.elements['experience-widget-guests'];
    if (guestsInput && Number.isFinite(availability)) {
      const originalMax = parseInt(guestsInput.getAttribute('data-original-max') ?? '', 10)
        || Infinity;
      const effectiveMax = Number.isFinite(originalMax)
        ? Math.min(availability, originalMax) : availability;
      guestsInput.max = effectiveMax > 0 ? String(effectiveMax) : '0';
      const guests = parseInt(guestsInput.value, 10);
      if (guests > effectiveMax && effectiveMax > 0) {
        this.showCapacityError(effectiveMax);
      } else {
        const errorEl = this.element.querySelector('.c-booking-widget__capacity-error');
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        this.capacityErrorShown = false;
      }
      // Don't dispatch 'change' on guests — would re-fetch and deselect the chosen slot.
      this.refreshStepperButtons();
    }
    if (this.form) this.form.dispatchEvent(new Event('change'));
  };

  // eslint-disable-next-line class-methods-use-this
  formatTime = (timeStr) => formatTime(timeStr);
}
